/**
 * Skeleton animation: `SkeletonPose` in, joint rotations out.
 *
 * There are no keyframes and no clips. Four states blend continuously by weight,
 * every weight smoothed at `SMOOTH_RATE`, so switching from *charging* to
 * *flail* mid-air eases rather than snapping — which matters because in this
 * game the state can change three times a second.
 *
 * This file owns the *joint contract* (`SkeletonParts`) and the handful of rig
 * dimensions the animation has to agree with (`RIG`). `skeleton.ts` builds the
 * meshes to match. Nothing here allocates per frame.
 *
 * Angle conventions, in each joint's local space:
 * - rotation about **+X** swings a limb *forward* (three.js forward is −Z);
 * - limbs hang along local −Y, so a knee bend is a *negative* X rotation;
 * - rotation about **+Z** swings an arm *outward*, away from the ribs.
 */

import { Color, Object3D, Quaternion, Vector3 } from 'three';
import type { MeshLambertMaterial } from 'three';
import { ANIM_AIRBORNE, ANIM_BOUNCE, ANIM_CHARGING, ANIM_FLAIL } from '@shared/protocol';
import type { SkeletonPose } from './rigTypes';

/** Rig dimensions the animation and the mesh builder must agree on, in metres. */
export const RIG = {
  /** Y of the top of the spring section, in stick-local space. */
  SPRING_TOP: -0.72,
  /** Length of the spring section at rest. */
  SPRING_LEN: 0.26,
  /** Y scale of the spring at full compression. */
  SPRING_MIN: 0.55,
  /** Extra shortening at full charge, as a scale — the pre-load crouch. */
  SPRING_CHARGE: 0.15,
};

/** How fast blended joint targets chase the current animation state, 1/s. */
const SMOOTH_RATE = 12;

/** One rig's animation memory. Created by `skeleton.ts`, owned by the rig. */
export interface AnimSmoothState {
  lastTime: number;
  first: boolean;
  wGround: number;
  wAir: number;
  wCharge: number;
  wFlail: number;
}

export function createAnimState(): AnimSmoothState {
  return { lastTime: 0, first: true, wGround: 1, wAir: 0, wCharge: 0, wFlail: 0 };
}

/**
 * Every joint the animation drives.
 *
 * Internal to the rig — `SkeletonRig` is the public contract, this is not. All
 * of these are pivot `Object3D`s with meshes parented under them, so the
 * animation rotates *joints*, never meshes.
 */
export interface SkeletonParts {
  /** Placed at the physics position and rotated to the stick axis + yaw. */
  root: Object3D;
  /** Counter-rotated to cancel `root`, so the name tag floats along world +Y. */
  tagAnchor: Object3D;
  /** Everything skeletal. Carries the squash/stretch scale and the spring sink. */
  body: Object3D;
  hips: Object3D;
  spine: Object3D;
  chest: Object3D;
  neck: Object3D;
  skull: Object3D;
  jaw: Object3D;
  shoulderL: Object3D;
  shoulderR: Object3D;
  elbowL: Object3D;
  elbowR: Object3D;
  hipL: Object3D;
  hipR: Object3D;
  kneeL: Object3D;
  kneeR: Object3D;
  ankleL: Object3D;
  ankleR: Object3D;
  /** Shaft, handle and pegs — sinks with the spring. */
  stickUpper: Object3D;
  /** Spring section: scales in Y, top follows `stickUpper`, bottom stays planted. */
  spring: Object3D;
  /** The stick's material, for the charge emissive pulse. */
  stickMat: MeshLambertMaterial;
  /** The player's hue, for the pulse colour. */
  tint: Color;
  anim: AnimSmoothState;
}

/** A symmetric set of joint angles. One per animation state. */
interface Preset {
  hip: number;
  knee: number;
  ankle: number;
  sh: number;
  shZ: number;
  el: number;
  spine: number;
  chest: number;
  neck: number;
  jaw: number;
}

// The arm angles are not decorative: with a 0.28 m upper arm and a 0.30 m
// forearm, `sh ≈ 1.0` / `el ≈ −2.0` folds the elbow enough to put the hands on
// the handle bar at stick-local y = +0.28. Likewise `hip + knee + ankle ≈ 0`
// keeps the feet flat on the pegs. Changing one of a pair without the other
// detaches the skeleton from its own stick.

/** Riding the stick: compact, hands on the bar, feet on the pegs, legs loaded. */
const GROUND: Preset = {
  hip: 0.45, knee: -0.90, ankle: 0.45,
  sh: 1.00, shZ: 0.16, el: -2.00,
  spine: 0.10, chest: 0.05, neck: -0.06, jaw: 0,
};

/** In flight: opened up, legs trailing, a little wind in the ribs. */
const AIR: Preset = {
  hip: 0.30, knee: -0.60, ankle: 0.30,
  sh: 0.92, shZ: 0.35, el: -1.85,
  spine: 0.04, chest: 0.02, neck: -0.10, jaw: 0.05,
};

/** Loaded: knees at 55°, spine 15° forward, arms tucked hard to the chest. */
const CHARGE: Preset = {
  hip: 0.75, knee: -0.96, ankle: 0.21,
  sh: 1.20, shZ: 0.06, el: -2.25,
  spine: 0.26, chest: 0.10, neck: 0.05, jaw: 0.10,
};

/** The comedy payload: arched back, head thrown back, jaw open 25°. */
const FLAIL: Preset = {
  hip: 0.05, knee: -0.30, ankle: 0.10,
  sh: 0.60, shZ: 0.90, el: -0.25,
  spine: -0.15, chest: -0.10, neck: -0.25, jaw: 0.44,
};

/** Blend accumulator. Module-level: this file allocates nothing per frame. */
const _b: Preset = { hip: 0, knee: 0, ankle: 0, sh: 0, shZ: 0, el: 0, spine: 0, chest: 0, neck: 0, jaw: 0 };

const UP = new Vector3(0, 1, 0);
const IDENTITY = new Quaternion();
const _qTilt = new Quaternion();
const _qYaw = new Quaternion();
const _qInv = new Quaternion();
const _qCounter = new Quaternion();
const _qUpright = new Quaternion();
const _upLocal = new Vector3();

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function addPreset(p: Preset, w: number): void {
  _b.hip += p.hip * w;
  _b.knee += p.knee * w;
  _b.ankle += p.ankle * w;
  _b.sh += p.sh * w;
  _b.shZ += p.shZ * w;
  _b.el += p.el * w;
  _b.spine += p.spine * w;
  _b.chest += p.chest * w;
  _b.neck += p.neck * w;
  _b.jaw += p.jaw * w;
}

/**
 * Apply one frame of animation to a rig.
 *
 * Called once per rendered frame per visible skeleton (the local player plus
 * every full-detail ghost), so it stays branch-light and allocation-free.
 */
export function applySkeletonPose(parts: SkeletonParts, pose: SkeletonPose): void {
  const s = parts.anim;
  const t = pose.timeSec;

  // --- state weights ------------------------------------------------------
  const dt = clamp(t - s.lastTime, 0, 0.25);
  s.lastTime = t;
  const k = s.first ? 1 : dt > 0 ? 1 - Math.exp(-SMOOTH_RATE * dt) : 0;
  s.first = false;

  const anim = pose.anim;
  s.wGround += ((anim === ANIM_BOUNCE ? 1 : 0) - s.wGround) * k;
  s.wAir += ((anim === ANIM_AIRBORNE ? 1 : 0) - s.wAir) * k;
  s.wCharge += ((anim === ANIM_CHARGING ? 1 : 0) - s.wCharge) * k;
  s.wFlail += ((anim === ANIM_FLAIL ? 1 : 0) - s.wFlail) * k;

  const total = s.wGround + s.wAir + s.wCharge + s.wFlail;
  const inv = total > 1e-4 ? 1 / total : 0;
  const wG = s.wGround * inv;
  const wA = s.wAir * inv;
  const wC = s.wCharge * inv;
  const wF = s.wFlail * inv;

  // --- root: position, stick tilt, yaw ------------------------------------
  parts.root.position.copy(pose.position);
  _qTilt.setFromUnitVectors(UP, pose.stickDir);
  _qYaw.setFromAxisAngle(UP, pose.yaw);
  parts.root.quaternion.copy(_qTilt).multiply(_qYaw);

  // The tag hangs off the root but must not swing 38° sideways with the lean.
  _qInv.copy(parts.root.quaternion).invert();
  parts.tagAnchor.quaternion.copy(_qInv);

  // --- spring compression, and the body sinking into it -------------------
  const compression = clamp(pose.compression, 0, 1);
  const charge = clamp(pose.charge, 0, 1);
  const springScale = clamp(
    (1 - (1 - RIG.SPRING_MIN) * compression) * (1 - RIG.SPRING_CHARGE * charge * wC),
    0.3,
    1,
  );
  // The spring's *bottom* is planted on the ground; everything above it drops.
  const travel = RIG.SPRING_LEN * (1 - springScale);
  parts.stickUpper.position.y = -travel;
  parts.spring.position.y = RIG.SPRING_TOP - travel;
  parts.spring.scale.y = springScale;

  // --- volume-preserving squash / stretch ---------------------------------
  const vy = clamp(pose.verticalVel, -15, 15);
  const sy = clamp(1 + vy * 0.02, 0.5, 1.6) * (1 - 0.1 * compression);
  const sxz = 1 / Math.sqrt(sy);
  parts.body.scale.set(sxz, sy, sxz);
  parts.body.position.y = -travel;

  // --- blend the four states ----------------------------------------------
  _b.hip = 0; _b.knee = 0; _b.ankle = 0;
  _b.sh = 0; _b.shZ = 0; _b.el = 0;
  _b.spine = 0; _b.chest = 0; _b.neck = 0; _b.jaw = 0;
  addPreset(GROUND, wG);
  addPreset(AIR, wA);
  addPreset(CHARGE, wC);
  addPreset(FLAIL, wF);

  // Impact compression folds the knees and elbows, whatever state we are in.
  _b.hip += 0.20 * compression;
  _b.knee -= 0.35 * compression;
  _b.el -= 0.25 * compression;

  // Idle bob, so a resting player is never a statue.
  const bob = Math.sin(t * 3.1) * 0.045 * wG;
  _b.spine += bob;
  _b.knee -= bob * 0.6;

  // Legs trail the motion while airborne.
  const trail = -pose.verticalVel * 0.012 * (wA + wF);
  _b.hip += clamp(trail, -0.35, 0.35);

  // --- write the joints ---------------------------------------------------
  // `.set`, not `.x =`: the counter-lean below writes a full quaternion back into
  // this euler, so clearing y/z every frame is what stops it accumulating.
  parts.spine.rotation.set(_b.spine, 0, 0);
  parts.chest.rotation.x = _b.chest;
  parts.neck.rotation.x = _b.neck;
  // The chin swings *down*, which is the opposite sense to a limb swinging forward.
  parts.jaw.rotation.x = -_b.jaw;

  // Spine counter-lean: the body tilts with the stick, the torso fights ~30% of
  // it back toward vertical, which is what stops the skeleton reading as a
  // rigidly welded lawn ornament.
  _upLocal.copy(UP).applyQuaternion(_qInv);
  _qUpright.setFromUnitVectors(UP, _upLocal);
  _qCounter.copy(IDENTITY).slerp(_qUpright, 0.3);
  parts.spine.quaternion.premultiply(_qCounter);

  parts.hipL.rotation.set(_b.hip, 0, 0);
  parts.hipR.rotation.set(_b.hip, 0, 0);
  parts.kneeL.rotation.x = _b.knee;
  parts.kneeR.rotation.x = _b.knee;
  parts.ankleL.rotation.x = _b.ankle;
  parts.ankleR.rotation.x = _b.ankle;

  // Windmilling arms, one half-cycle apart. This is the whole joke.
  const wind = Math.sin(t * 18) * 1.92 * wF;
  const windR = Math.sin(t * 18 + Math.PI) * 1.92 * wF;
  parts.shoulderL.rotation.set(_b.sh + wind, 0, _b.shZ);
  parts.shoulderR.rotation.set(_b.sh + windR, 0, -_b.shZ);
  parts.elbowL.rotation.x = _b.el - 0.35 * wF * (0.5 + 0.5 * Math.sin(t * 18 + 1.1));
  parts.elbowR.rotation.x = _b.el - 0.35 * wF * (0.5 + 0.5 * Math.sin(t * 18 + 1.1 + Math.PI));

  // Skull jitter: ±4°, only while flailing.
  parts.skull.rotation.set(
    Math.sin(t * 29.3) * 0.07 * wF,
    Math.sin(t * 23.7) * 0.07 * wF,
    Math.sin(t * 41.1) * 0.07 * wF,
  );

  // --- charge pulse -------------------------------------------------------
  const hz = 2 + charge * 6;
  const pulse = charge * (0.35 + 0.65 * (0.5 + 0.5 * Math.sin(t * Math.PI * 2 * hz)));
  parts.stickMat.emissive.copy(parts.tint).multiplyScalar(pulse);
}
